import { ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { RHFValidations, TOAST_MESSAGES } from '../../constants/global-constants';
import authenticateService from '../../services/authenticateService';
import CustomInputBox from '../../controls/CustomInputBox';
import parsePasswordValidationMap from '../../_helpers/passwordValidation';
import { AwsLoginErrors } from '../../constants/telosair-interfaces';
import PasswordInputBox from '../../controls/PasswordInputBox';

type FORMDATA = {
  password: string;
  confirmPassword: string;
  tempPassword: string;
};

/**
 * ForgotPassword.
 * @returns - React component for ForgotPassword UI.
 */
export default function ResetTempPasswordComponent(): ReactElement {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const username = params.get('email') || '';
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const methods = useForm<FORMDATA>({
    mode: 'all'
  });

  const { handleSubmit, watch } = methods;

  /**
   * Handles the Submit.
   * @param data -The list used to get data.
   */
  const onhandleSubmit = async (data: FORMDATA): Promise<void> => {
    try {
      if (!username) {
        toast.error('Please try again');
        navigate(-1);
        return;
      }
      setLoading(true);
      const decodedUsername = decodeURIComponent(username);
      const decodedToken = decodeURIComponent(token);
      const { error } = await authenticateService.resetTemporaryPassword(
        decodedUsername,
        data.tempPassword,
        data.password,
        decodedToken
      );
      if (!error) {
        toast.success('Password reset successful, please login again with your new password');
        navigate(-1);
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.invalidCredentials) {
        toast.error("Temporary password isn't correct");
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.notFound) {
        toast.error('User not found');
        setLoading(false);
        return;
      }
      toast.error(TOAST_MESSAGES.error);
      setLoading(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const confirmPasswordValidation = parsePasswordValidationMap(
    'confirm password',
    watch('password')
  );

  return (
    <div className="flex flex-col gap-8 w-full">
      <div className="text-3xl font-bold dark:text-white text-black text-center">
        Welcome to TelosAir
      </div>
      {/* <div className="text-gray-400 mt-1">Start managing your devices for free</div> */}
      <div className="dark:bg-container-700 bg-white p-6 rounded w-full">
        <div className="flex gap-3 items-center">
          <button
            onClick={() => navigate(-1)}
            type="button"
            className="font-medium text-gray-300 text-xl material-icons-outlined"
          >
            arrow_back
          </button>
          <div className="text-xl dark:text-white text-black font-semibold">Set Password</div>
        </div>
        <div className="text-gray-400 mt-1">Please set a password for your account.</div>
        {/* eslint-disable-next-line */}
        <FormProvider {...methods}>
          <form className="mt-8 2xl:mt-12" action="post" onSubmit={handleSubmit(onhandleSubmit)}>
            <CustomInputBox
              label="Temporary Password"
              idname="tempPassword"
              autoComplete="off"
              type="text"
              validationObject={{ required: 'Please enter the temporary password' }}
            />
            <PasswordInputBox
              label="Password"
              idname="password"
              autoComplete="new-password"
              validationObject={RHFValidations.password}
            />
            <PasswordInputBox
              label="Confirm Password"
              idname="confirmPassword"
              autoComplete="off"
              validationObject={confirmPasswordValidation}
            />
            <button
              type="submit"
              className="w-full relative mt-12 text-white bg-primary-600 hover:bg-primary-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg flex justify-center items-center text-sm px-5 py-2.5 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
            >
              {loading ? <ClipLoader size={20} color="#fff" /> : 'Reset Password'}
            </button>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
